
/**
 * Utility functions to calculate property investment score
 */

// Calculate the overall property score on a scale from 0-100
export function calculatePropertyScore(analysis: any): number {
  if (!analysis) return 50; // Default neutral score if no analysis available

  // Componente A: Comparação de Preço (40%)
  const priceScore = calculatePriceComparisonScore(
    analysis.property_info.valor_recente,
    analysis.analysis.neighborhood_stats.avg_price
  );
  
  // Componente B: Comparação de Preço por m² (30%)
  const pricePerSqMScore = calculatePricePerSqMComparisonScore(
    analysis.property_info.valor_recente / analysis.property_info.area_util,
    analysis.analysis.neighborhood_stats.avg_price_per_m2
  );
  
  // Componente C: Balanço entre Pontos Fortes e Fracos (20%)
  const strengthWeaknessScore = calculateStrengthWeaknessScore(
    analysis.evaluation.strengths.length,
    analysis.evaluation.weaknesses.length
  );
  
  // Componente D: Características Especiais (10%)
  const specialFeaturesScore = calculateSpecialFeaturesScore(
    analysis.evaluation.strengths,
    analysis.property_info.descricao
  );
  
  // Cálculo ponderado do score final
  const finalScore = (
    priceScore * 0.2 + 
    pricePerSqMScore * 0.5 + 
    strengthWeaknessScore * 0.2 + 
    specialFeaturesScore * 0.1
  );
  
  // Garantir que o score esteja entre 0 e 100
  return Math.round(Math.max(0, Math.min(100, finalScore)));
}

// Calculate score based on property price compared to neighborhood average (40%)
function calculatePriceComparisonScore(propertyPrice: number, avgPrice: number): number {
  if (!avgPrice || avgPrice <= 0) return 50; // valor neutro se não houver dados
  
  const priceDiff = (avgPrice - propertyPrice) / avgPrice;
  
  // Se o preço está abaixo da média, recebe pontuação mais alta
  if (priceDiff > 0) {
    // Máximo de 100 pontos se estiver mais de 20% abaixo da média
    return Math.min(100, 50 + priceDiff * 250);
  } else {
    // Mínimo de 0 pontos se estiver mais de 20% acima da média
    return Math.max(0, 50 + priceDiff * 250);
  }
}

// Calculate score based on price per square meter comparison (30%)
function calculatePricePerSqMComparisonScore(propertyPricePerSqM: number, avgPricePerSqM: number): number {
  if (!avgPricePerSqM || avgPricePerSqM <= 0) return 50; // valor neutro se não houver dados
  
  const priceDiff = (avgPricePerSqM - propertyPricePerSqM) / avgPricePerSqM;
  
  // Se o preço por m² está abaixo da média, recebe pontuação mais alta
  if (priceDiff > 0) {
    // Máximo de 100 pontos se estiver mais de 20% abaixo da média
    return Math.min(100, 50 + priceDiff * 250);
  } else {
    // Mínimo de 0 pontos se estiver mais de 20% acima da média
    return Math.max(0, 50 + priceDiff * 250);
  }
}

// Calculate score based on the balance between strengths and weaknesses (20%)
function calculateStrengthWeaknessScore(strengthsCount: number, weaknessesCount: number): number {
  if (strengthsCount === 0 && weaknessesCount === 0) return 50;
  
  // Calcular proporção entre pontos fortes e fracos
  const total = strengthsCount + weaknessesCount;
  const strengthProportion = strengthsCount / total;
  
  // Converter para uma escala de 0-100
  return strengthProportion * 100;
}

// Calculate score based on special features of the property (10%)
function calculateSpecialFeaturesScore(strengths: string[], description: string | null): number {
  if (!strengths.length && !description) return 50;
  
  let score = 50; // Pontuação inicial neutra
  
  // Palavras-chave que indicam características especiais
  const specialKeywords = [
    'novo', 'reformado', 'mobiliado', 'vista', 'piscina', 'academia', 'próximo', 
    'metrô', 'shopping', 'parque', 'segurança', 'varanda', 'gourmet', 'exclusivo'
  ];
  
  // Verificar pontos fortes para características especiais
  strengths.forEach(strength => {
    for (const keyword of specialKeywords) {
      if (strength.toLowerCase().includes(keyword)) {
        score += 5; // +5 pontos para cada palavra-chave encontrada
        break;
      }
    }
  });
  
  // Verificar descrição para características especiais
  if (description) {
    const descriptionLower = description.toLowerCase();
    for (const keyword of specialKeywords) {
      if (descriptionLower.includes(keyword)) {
        score += 3; // +3 pontos para cada palavra-chave encontrada na descrição
      }
    }
  }
  
  // Limitar o score a 100
  return Math.min(100, score);
}

// Helper function to get appropriate text based on score
export function getScoreText(score: number): string {
  if (score >= 80) return 'Considere o investimento';
  if (score >= 60) return 'Vale explorar';
  return 'Avalie com cautela';
}

// Helper function to get appropriate color class based on score
export function getScoreColorClass(score: number): string {
  if (score >= 80) return 'text-green-500';
  if (score >= 60) return 'text-yellow-500';
  return 'text-red-500';
}
